import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import EditIcon from '@mui/icons-material/Edit'
import LockResetIcon from '@mui/icons-material/LockReset'
import { groupBy } from 'lodash'
import { Fragment, useContext, useEffect, useRef, useState } from 'react'
import {
	Box,
	Button,
	IconButton,
	Typography,
	Tooltip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
} from '@mui/material'

import { useMeta } from '@contexts/Meta'
import { useDialog } from '@hooks/useDialog'
import useAPIFetch from '@hooks/useAPIFetch'
import { UserList } from '@models/user.model'
import useIsMounted from '@hooks/useIsMounted'
import { OptionsContext } from '@contexts/Options'
import UserService from '@services/users/user.service'
import AuthService from '@services/users/auth.service'
import { TableView } from '@components/customized/datatable'
import { sortByName } from '@utils/sortByName'
import { AuthContext } from '@contexts/Auth'
import { PermissionCodeEnum } from '@models/permission'
import { hasPermission } from '@utils/permission_helper'
import CommonService from '@services/common/common.service'
import * as XLSX from 'xlsx-js-style'
import { DateRange, DateRangeEditor } from 'material-mui-date-range-picker'
import moment from 'moment'

export default function UsersTable() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { enqueueSnackbar } = useSnackbar()
	const [resetPwUser, setresetPwUser] = useState<string>('')
	const { state, dispatch } = useContext(OptionsContext)
	const isMounted = useIsMounted()
	const [, { setMeta }] = useMeta()
	const { setRequest, isLoading } = useAPIFetch()
	const { setRequest: setExportRequest, isLoading: isExportLoading } = useAPIFetch()
	const { setRequest: setExportAuditRequest, isLoading: isExportAuditLoading } = useAPIFetch()
	const refMounted = useRef(true)
	const [filterOptions, setFilterOptions] = useState<any>({})
	const { state: authState } = useContext(AuthContext)
	const [openEexportAudit, setOpenEexportAudit] = useState(false)
	const [dateRange, setDateRange] = useState<DateRange>({
		startDate: moment().startOf('month').toDate(),
		endDate: moment().endOf('month').toDate(),
	})

	useEffect(() => {
		setMeta({ isLoading })
	}, [isLoading])

	const [CustomizatedDialog, controlDialog] = useDialog({
		title: t('Reset Password'),
		children: (
			<>
				<Typography>{t('You are going to reset password of this user')}</Typography>
			</>
		),
		closeButton: true,
		actionCloseButton: true,
		actionConfirmButton: true,
		actionOnClose: () => {
			setresetPwUser('')
		},
		actionOnConfirm: () => {
			setRequest({
				callback: async (cancelToken: any) => {
					await AuthService.ResetPassword(resetPwUser)
						.then(() => {
							enqueueSnackbar(t('Email of reset password has sent to the user'), {
								variant: 'success',
							})
							return
						})
						.catch((error) => {
							enqueueSnackbar(error.detail, {
								variant: 'error',
							})
						})
					setresetPwUser('')
				},
			})
		},
	})

	useEffect(() => {
		if (resetPwUser !== '') controlDialog()
	}, [resetPwUser])

	useEffect(() => {
		UserService.GetUsersOptions({}).then((rep) => {
			setFilterOptions(rep)
		})
	}, [])

	const exportUser = async () => {
		setExportRequest({
			callback: async (cancelToken: any) => {
				// fetch role optipns list first.
				const roleList = await CommonService.GetCategoryMetadata('role')

				const projectListResp = await UserService.GetMyProfileProjectList({})
				const projectList = projectListResp?.data || []
				// fetch user list.
				const userResp = await UserService.GetUserList({
					active: {
						operator: '=',
						value: [true],
					},
				})

				const wb = XLSX.utils.book_new()

				let rows: any[] = []
				// add header row
				let headers: any[] = [
					{ v: 'Login', t: 's', s: { font: { name: 'Arial', sz: 18 }, innerWidth: '50px' } },
					{ v: 'Display Name', t: 's', s: { font: { name: 'Arial', sz: 18 } } },
					{ v: 'Active', t: 's', s: { font: { name: 'Arial', sz: 18 } } },
					{ v: 'Dept/Team', t: 's', s: { font: { name: 'Arial', sz: 18 } } },
				]

				projectList.forEach((proj) => {
					headers.push({ v: proj.code, t: 's', s: { font: { name: 'Arial', sz: 18 } } })
				})

				roleList.forEach((role) => {
					headers.push({ v: role.value, t: 's', s: { font: { name: 'Arial', sz: 18 } } })
				})

				rows.push(headers)

				// add data row
				userResp.data.forEach((u) => {
					let cells = [
						{ v: u.email, t: 's' },
						{ v: `${u.first_name} ${u.last_name}`, t: 's' },
						{ v: u.base_system_field?.active ? 'ACTIVE' : 'INACTIVE', t: 's' },
						{ v: u.position, t: 's' },
					]

					const projectIds: string[] = []
					u.product_module_subs?.forEach((sub) => {
						projectIds.push(sub.project_id)
					})

					projectList?.forEach((p) => {
						cells.push({ v: projectIds?.find((x) => x === p.project_id) ? 'V' : '', t: 's' })
					})

					roleList?.forEach((role) => {
						cells.push({ v: u.role_ids?.find((x) => x === role.key) ? 'V' : '', t: 's' })
					})

					rows.push(cells)
				})

				rows.push([])
				rows.push([
					{
						v: 'Generated date',
						t: 's',
						s: { font: { name: 'Arial', sz: 12 }, innerWidth: '50px' },
					},
					{
						v: moment().format('DD-MMM-yyyy HH:mm:ss'),
						t: 's',
						s: { font: { name: 'Arial', sz: 12 }, innerWidth: '50px' },
					},
				])

				// create worksheet with rows
				const ws = XLSX.utils.aoa_to_sheet(rows)
				if (!ws['!cols']) ws['!cols'] = []
				if (!ws['!cols'][0]) ws['!cols'][0] = { wch: 50 }
				if (!ws['!cols'][1]) ws['!cols'][1] = { wch: 40 }
				if (!ws['!cols'][2]) ws['!cols'][2] = { wch: 15 }
				if (!ws['!cols'][3]) ws['!cols'][3] = { wch: 25 }
				if (!ws['!cols'][4]) ws['!cols'][4] = { wch: 25 }
				if (!ws['!cols'][5]) ws['!cols'][5] = { wch: 30 }

				if (!ws['!rows']) ws['!rows'] = []
				if (!ws['!rows'][0]) ws['!rows'][0] = { hpx: 50 }

				// Add worksheet to workbook
				XLSX.utils.book_append_sheet(wb, ws, 'User List')
				/* export to XLSX */
				XLSX.writeFile(wb, `${moment().format('DD-MMM-yyyy HH:mm:ss')}_user_list.xlsx`)
			},
		})
	}

	const exportAuditTrails = async () => {
		setExportRequest({
			callback: async (cancelToken: any) => {
				// fetch audit trails list.
				const resp = await UserService.GetAuditTrails({
					date: {
						operator: 'in',
						value: {
							min: dateRange?.startDate?.toISOString(),
							max: dateRange?.endDate?.toISOString(),
						},
					},
				})

				const wb = XLSX.utils.book_new()
				let rows: any[] = []
				// add header row
				let headers: any[] = [
					{ v: 'Date', t: 's', s: { font: { name: 'Arial', sz: 18 }, innerWidth: '30px' } },
					{ v: 'Project', t: 's', s: { font: { name: 'Arial', sz: 18 } } },
					{ v: 'Event type', t: 's', s: { font: { name: 'Arial', sz: 18, innerWidth: '40px' } } },
					{ v: 'User', t: 's', s: { font: { name: 'Arial', sz: 18 } } },
					{ v: 'Message', t: 's', s: { font: { name: 'Arial', sz: 18, innerWidth: '50px' } } },
					{ v: 'Status', t: 's', s: { font: { name: 'Arial', sz: 18 } } },
				]

				rows.push(headers)

				// add data row
				resp.data.data.forEach((at) => {
					let cells = [
						{ v: moment(at.date).format('yyyy-DDD-mm HH:mm'), t: 's' },
						{ v: at.projectLabel ? `${at.projectLabel}` : '', t: 's' },
						{ v: at.eventType, t: 's' },
						{
							v:
								at.user &&
								`${at.user.firstName ? at.user.firstName : ''} 
								${at.user.lastName ? at.user.lastName : ''}`,
							t: 's',
						},
						{ v: at.message, t: 's' },
						{ v: at.status, t: 's' },
					]

					rows.push(cells)
				})

				// create worksheet with rows
				const ws = XLSX.utils.aoa_to_sheet(rows)
				if (!ws['!cols']) ws['!cols'] = []
				if (!ws['!cols'][0]) ws['!cols'][0] = { wch: 20 }
				if (!ws['!cols'][1]) ws['!cols'][1] = { wch: 30 }
				if (!ws['!cols'][2]) ws['!cols'][2] = { wch: 20 }
				if (!ws['!cols'][3]) ws['!cols'][3] = { wch: 25 }
				if (!ws['!cols'][4]) ws['!cols'][4] = { wch: 50 }
				if (!ws['!cols'][5]) ws['!cols'][5] = { wch: 20 }

				if (!ws['!rows']) ws['!rows'] = []
				if (!ws['!rows'][0]) ws['!rows'][0] = { hpx: 50 }

				// Add worksheet to workbook
				XLSX.utils.book_append_sheet(wb, ws, 'Audit trails')
				/* export to XLSX */
				XLSX.writeFile(wb, `${new Date().toDateString()}_audit_trails.xlsx`)

				setOpenEexportAudit(false)
			},
		})
	}

	return (
		<>
			<TableView
				columns={[
					{
						key: 'name',
						desc: t('Name'),
						width: 200,
						renderCell: (params: UserList) => {
							return (
								<Fragment>
									<Tooltip title={params?.first_name + ' ' + params?.last_name}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{params?.first_name + ' ' + params?.last_name}
										</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'company',
						desc: t('Organisation'),
						width: 180,
						renderCell: (params: UserList) => {
							return (
								<Fragment>
									<Tooltip title={params?.company}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{params?.company?.length > 18
												? params?.company.substring(0, 18) + '...'
												: params?.company}
										</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'product_module_subs',
						desc: t('Projects'),
						width: 400,
						disableSort: true,
						renderCell: (params: UserList) => {
							const groupByProjects = groupBy(params?.product_module_subs, 'project_id')
							const result = Object.keys(groupByProjects).map((project) => {
								const project_subs = groupByProjects[project].map((x) => {
									switch (x.item_type) {
										case 'product':
											return state.product?.find((product) => product.key === x.item_id)?.value
										case 'module':
											return state.module?.find((module) => module.key === x.item_id)?.value
									}
								})
								return (
									state.project?.find((x) => x.key === project)?.value +
									' - ' +
									project_subs.join(', ')
								)
							})

							result?.sort((a: any, b: any) => {
								if (a < b) {
									return -1
								}
								if (a > b) {
									return 1
								}
								return 0
							})

							return (
								<>
									<Tooltip
										title={<Box sx={{ whiteSpace: 'break-spaces' }}>{result?.join('\n')}</Box>}
									>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{result.join('\n')?.length > 0
												? result.join('\n').substring(0, 40) + '...'
												: result}
										</Box>
									</Tooltip>
								</>
							)
						},
					},
					{
						key: 'position',
						desc: t('Dept/Team'),
						width: 150,
						renderCell: (params: UserList) => {
							return (
								<Fragment>
									<Tooltip title={params?.position}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{params?.position?.length > 15
												? params?.position.substring(0, 15) + '...'
												: params?.position}
										</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'email',
						desc: t('Email'),
						width: 250,
						renderCell: (params: UserList) => {
							return (
								<Fragment>
									<Tooltip title={params?.email}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{params?.email?.length > 20
												? params?.email.substring(0, 20) + '...'
												: params?.email}
										</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'phone',
						desc: t('Phone'),
						width: 150,
						renderCell: (params: UserList) => {
							return (
								<Fragment>
									<Tooltip title={params?.phone}>
										<Box sx={{ 'white-space': 'break-spaces' }}>
											{params?.phone?.length > 15
												? params?.phone.substring(0, 15) + '...'
												: params?.phone}
										</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'active',
						desc: t('Active'),
						width: 100,
						renderCell: (params: UserList) => {
							const activeStatus = params?.base_system_field?.active ? 'Activated' : 'Deactivated'

							return (
								<Fragment>
									<Tooltip title={activeStatus}>
										<Box sx={{ 'white-space': 'break-spaces' }}>{activeStatus}</Box>
									</Tooltip>
								</Fragment>
							)
						},
					},
					{
						key: 'id',
						desc: t('Function Buttons'),
						width: 150,
						renderCell: (params: UserList) => {
							return (
								<Box sx={{ 'white-space': 'break-spaces' }}>
									{hasPermission(authState?.userInfo, [PermissionCodeEnum.UserUpdate]) && (
										<IconButton
											onClick={() =>
												navigate(`/configurations/users_config/users/edit_user/${params?.id}`)
											}
										>
											<Tooltip title={'Edit User'}>
												<EditIcon>{t('Edit')}</EditIcon>
											</Tooltip>
										</IconButton>
									)}

									{hasPermission(authState?.userInfo, [PermissionCodeEnum.UserResetPassword]) && (
										<IconButton>
											<Tooltip title={'Reset Password'} onClick={() => setresetPwUser(params?.id)}>
												<LockResetIcon>{t('Reset')}</LockResetIcon>
											</Tooltip>
										</IconButton>
									)}
								</Box>
							)
						},
						disableSort: true,
					},
				]}
				reloadCallback={async (req: any) => {
					console.log(req)
					if (refMounted.current && isMounted()) {
						let resp = await UserService.GetUserList(req)
						return resp
					}
				}}
				toolbar={true}
				mountedRef={refMounted}
				extraButton={
					<>
						{hasPermission(authState?.userInfo, [PermissionCodeEnum.UserCreate]) ? (
							<Button
								variant="contained"
								onClick={() => navigate('/configurations/users_config/users/create_user')}
								color="defaultButton"
							>
								{t('Create User')}
							</Button>
						) : undefined}

						{hasPermission(authState?.userInfo, [PermissionCodeEnum.UserExport]) ? (
							<Button
								variant="contained"
								onClick={exportUser}
								color="success"
								startIcon={isExportLoading && <CircularProgress size={'0.5rem'} />}
								disabled={isExportLoading}
							>
								{t('Export')}
							</Button>
						) : undefined}

						{/* {hasPermission(authState?.userInfo, [PermissionCodeEnum.AuditTrailsRead]) ? (
							<Button
								variant="contained"
								onClick={() => setOpenEexportAudit(true)}
								color="primary"
								startIcon={isExportAuditLoading && <CircularProgress size={'0.5rem'} />}
								disabled={isExportAuditLoading}
							>
								{t('Export Audit trails')}
							</Button>
						) : undefined} */}
					</>
				}
				haveGlobalSearch={true}
				criterion={[
					{
						key: 'name',
						name: t('Name'),
						type: 'StringFilter',
					},
					{
						key: 'org_ids',
						name: t('Organisation'),
						type: 'SelectionFilter',
						options: state.organization,
					},
					// {
					// 	key: 'profile_ids',
					// 	name: t('Profile'),
					// 	type:  'SelectionFilter',
					// 	options: state.profile
					// },
					{
						key: 'project_ids',
						name: t('Projects'),
						type: 'SelectionFilter',
						// options: state.project,
						options: sortByName(state.project)?.filter((elem: any) => {
							return filterOptions.project_ids?.includes(elem.key)
						}),
					},
					// {
					// 	key: 'role_ids',
					// 	name: t('User Group'),
					// 	type:  'SelectionFilter',
					// 	options: state.role
					// },
					{
						key: 'position',
						name: t('Position'),
						type: 'StringFilter',
					},
					{
						key: 'email',
						name: t('Email'),
						type: 'StringFilter',
					},
					{
						key: 'phone',
						name: t('Phone'),
						type: 'StringFilter',
					},
					{
						key: 'active',
						name: t('Active'),
						type: 'ActiveFilter',
					},
					// {
					// 	key: 'is_deleted',
					// 	name: t('Is Deleted'),
					// 	type:  'SelectionFilter',
					// 	options: [{key: 'true', value: 'true'}, {key: 'false', value: 'false'}]
					// },
				]}
				defaultFilterCriteria={{ active: { operator: '=', value: [true, false] } }}
				defaultSorter={{ by: 'name', order_by: 'DESC' }}
			/>
			<CustomizatedDialog />

			<Dialog
				open={openEexportAudit}
				onClose={() => setOpenEexportAudit(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					component: 'form',
					onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
						event.preventDefault()
						console.log(dateRange)
						exportAuditTrails()
					},
				}}
			>
				<DialogTitle>{t('Export audit trails')}</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							'& .MuiPaper-elevation5': {
								position: 'fixed',
							},
						}}
					>
						<DateRangeEditor dateRange={dateRange} onChange={(range) => setDateRange(range)} />
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						type="submit"
						color="success"
						style={{
							background: 'green',
						}}
					>
						Export
					</Button>
					<Button
						onClick={() => setOpenEexportAudit(false)}
						style={{
							background: 'grey',
						}}
					>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
